import axios from "axios";

/**
 * @typedef {Object} SdkKeys
 * @property {string|null} googleApiKey
 * @property {string|null} pendoApiKey
 * @property {string|null} launchDarklyKey
 */

const NULL_KEYS = { googleApiKey: null, pendoApiKey: null, launchDarklyKey: null };

const RETRY_DELAY_MS = 2000;

/**
 * Delays execution for the specified number of milliseconds.
 * @param {number} ms
 * @returns {Promise<void>}
 */
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Determines whether the error is retryable (network error or HTTP 500/503).
 * @param {object} error - Axios error
 * @returns {boolean}
 */
function isRetryable(error) {
  if (!error.response) return true; // network error
  const status = error.response.status;
  return status === 500 || status === 503;
}

/**
 * Fetches SDK keys from the backend. Resolves with key data on success or
 * all-null on non-auth failures. Rejects only on 401/403 so the interceptor
 * can handle session expiration.
 *
 * @returns {Promise<SdkKeys>}
 */
export async function fetchSdkKeys() {
  const url = `${global.REACT_APP_API_ENDPOINT}/metadata/sdk-keys`;

  try {
    const response = await axios.get(url);
    console.info("SDK keys fetched successfully");
    return response.data;
  } catch (error) {
    // 401/403 — let the interceptor handle auth errors
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      return Promise.reject(error);
    }

    // Retryable: network error or 500/503
    if (isRetryable(error)) {
      const status = error.response ? error.response.status : "network error";
      console.warn(`SDK keys fetch failed (${status}), retrying in 2s...`);

      await delay(RETRY_DELAY_MS);

      try {
        const retryResponse = await axios.get(url);
        console.info("SDK keys fetched successfully");
        return retryResponse.data;
      } catch (retryError) {
        // 401/403 on retry — still propagate
        if (retryError.response && (retryError.response.status === 401 || retryError.response.status === 403)) {
          return Promise.reject(retryError);
        }
        console.error("SDK keys fetch failed after retry — proceeding without SDK keys");
        return NULL_KEYS;
      }
    }

    // Any other HTTP error (4xx, other 5xx) — resolve with null keys immediately
    return NULL_KEYS;
  }
}
